import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';

import type { IncidentCustomer } from '@models/incidents';
import { UserPermissions } from '@models/user';
import type { GetIncidentCustomersParams } from '@features/incidents/api';
import {
  INCIDENTS_PATH,
  INCIDENT_STATUS_LABELS,
  getIncidentStatusTone,
} from '@features/incidents/constants';
import { useDetachIncidentCustomerMutation } from '@features/incidents/mutations';
import { useIncidentCustomersQuery, useIncidentQuery } from '@features/incidents/queries';
import { useMeStore } from '@features/users/store';
import { useConfirm } from '@hooks/useConfirm';
import { useToast } from '@hooks/useToast';
import { userHasPermission } from '@utils/access';
import { formatRigaDateTime } from '@utils/date';

import LoadingAnimation from '@components/LoadingAnimation';
import Pagination from '@components/Pagination';
import PrimaryButton from '@components/PrimaryButton';
import SecondaryButton from '@components/SecondaryButton';
import StatusChip from '@components/ui/StatusChip';
import AttachCustomersDialog from '@components/Incidents/AttachCustomersDialog';
import IncidentCustomersTable from '@components/Incidents/IncidentCustomersTable';
import IncidentFormDialog from '@components/Incidents/IncidentFormDialog';
import s from './IncidentDetailPage.module.css';

const DEFAULT_ROWS_PER_PAGE = 25;

function IncidentDetailPage() {
  const { incidentId } = useParams<{ incidentId: string }>();
  const toast = useToast();
  const confirm = useConfirm();
  const me = useMeStore();
  const canEdit = !!me && userHasPermission(me, UserPermissions.EDIT_USERS);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [editOpen, setEditOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);

  const incidentQuery = useIncidentQuery(incidentId);
  const customersParams: GetIncidentCustomersParams = { page: page + 1, limit: rowsPerPage };
  const customersQuery = useIncidentCustomersQuery(incidentId, customersParams);
  const detachCustomer = useDetachIncidentCustomerMutation();

  const errorShownRef = useRef(false);
  useEffect(() => {
    const failed = incidentQuery.isError || customersQuery.isError;
    if (failed && !errorShownRef.current) {
      toast.error('Failed to load incident');
      errorShownRef.current = true;
    }
    if (!failed) errorShownRef.current = false;
  }, [incidentQuery.isError, customersQuery.isError, toast]);

  const incident = incidentQuery.data;

  const handleDetach = async (row: IncidentCustomer) => {
    if (!incidentId) return;
    const ok = await confirm(
      `Detach ${row.email} from this incident? The journal will no longer show the customer as affected.`,
      { title: 'Detach customer', confirmLabel: 'Detach', tone: 'danger' },
    );
    if (!ok) return;
    detachCustomer.mutate({ incidentId, customerId: row.customerId });
  };

  if (incidentQuery.isLoading) {
    return (
      <div className={s.container}>
        <LoadingAnimation compact />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className={s.container}>
        <Link to={INCIDENTS_PATH} className={s.backLink}>
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          All incidents
        </Link>
        <div className={s.notFound}>Incident not found.</div>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <Link to={INCIDENTS_PATH} className={s.backLink}>
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        All incidents
      </Link>

      <header className={s.header}>
        <div className={s.headerBody}>
          <div className={s.titleRow}>
            <h1 className={s.title}>{incident.name}</h1>
            <StatusChip
              label={INCIDENT_STATUS_LABELS[incident.status] ?? incident.status}
              tone={getIncidentStatusTone(incident.status)}
            />
          </div>
          {incident.description && <p className={s.description}>{incident.description}</p>}
          <div className={s.metaRow}>
            <span className={s.metaItem}>
              <span className={s.metaValue}>{incident.involvedCustomerCount.toLocaleString()}</span>{' '}
              customer{incident.involvedCustomerCount === 1 ? '' : 's'} involved
            </span>
            <span className={s.metaDot} aria-hidden />
            <span className={s.metaItem}>
              Created {formatRigaDateTime(incident.createdAt)}
              {incident.createdBy ? ` by ${incident.createdBy.name}` : ''}
            </span>
            {incident.updatedAt && incident.updatedAt !== incident.createdAt && (
              <>
                <span className={s.metaDot} aria-hidden />
                <span className={s.metaItem}>Updated {formatRigaDateTime(incident.updatedAt)}</span>
              </>
            )}
            <span className={s.metaDot} aria-hidden />
            <span className={`${s.metaItem} ${s.mono}`} title={incident.id}>
              {incident.id}
            </span>
          </div>
        </div>

        {canEdit && (
          <div className={s.actions}>
            <SecondaryButton
              title="Edit"
              icon={<EditOutlinedIcon />}
              onClick={() => setEditOpen(true)}
            />
            <PrimaryButton
              title="Attach customers"
              icon={<PersonAddAlt1Icon />}
              onClick={() => setAttachOpen(true)}
            />
          </div>
        )}
      </header>

      <IncidentCustomersTable
        isLoading={customersQuery.isLoading}
        isFetching={customersQuery.isFetching}
        rows={customersQuery.data?.data ?? []}
        onDetach={canEdit ? handleDetach : undefined}
      />

      {!customersQuery.isLoading && (
        <Pagination
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          total={customersQuery.data?.meta.itemCount ?? 0}
        />
      )}

      {canEdit && (
        <>
          <IncidentFormDialog
            open={editOpen}
            onClose={() => setEditOpen(false)}
            incident={incident}
          />
          <AttachCustomersDialog
            open={attachOpen}
            onClose={() => setAttachOpen(false)}
            incidentId={incident.id}
          />
        </>
      )}
    </div>
  );
}

export default IncidentDetailPage;
